import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { API_BASE_URL } from "../../../config";

function EditPoolCareListings() {
  const { id } = useParams();
  const navigate = useNavigate();


  const [message, setMessage] = useState("");

  // ================================
  // BASIC LOCATION
  // ================================
  const [suburb, setSuburb] = useState("");
  const [state, setState] = useState("");
  const [postcode, setPostcode] = useState("");

  // ================================
  // CLIENT OVERVIEW
  // ================================
  const [accessType, setAccessType] = useState("Access Available Anytime");
  const [streetParking, setStreetParking] = useState(false);
  const [petsAtHome, setPetsAtHome] = useState(false);
  const [alarmCodeRequired, setAlarmCodeRequired] = useState(false);

  // ================================
  // POOL DETAILS
  // ================================
  const [poolType, setPoolType] = useState("");
  const [poolSize, setPoolSize] = useState("");
  const [poolSurface, setPoolSurface] = useState("");
  const [hasSpa, setHasSpa] = useState(false);

  const [newClient, setNewClient] = useState(false);
  const [longTermClient, setLongTermClient] = useState(false);

  // ================================
  // SERVICE PREFERENCES
  // ================================
  const [serviceDay, setServiceDay] = useState("any");
  const [serviceTime, setServiceTime] = useState("any");
  const [frequency, setFrequency] = useState("weekly");

  // ================================
  // POOL SERVICES
  // ================================
  const [services, setServices] = useState({
    waterTesting: true,
    chemicalBalancing: true,
    chlorineSupply: false,
    saltCellCleaning: false,
    vacuuming: true,
    skimming: true,
    brushingWalls: true,
    filterCleaning: false,
    acidWash: false,
    equipmentCheck: true,
  });

  const toggleService = (key) => {
    setServices((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // ================================
  // PRICING
  // ================================
  const [serviceFee, setServiceFee] = useState("");
  const [salePrice, setSalePrice] = useState("");

  // ================================
  // LOAD LISTING
  // ================================
  useEffect(() => {
    async function loadListing() {
      try {
        const token = localStorage.getItem("token");

        const res = await fetch(
          `${API_BASE_URL}/api/poolcare/listings/${id}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const data = await res.json();

        if (!data?.success || !data?.listing) {
          alert("Listing not found");
          navigate("/my-listings");
          return;
        }

        const l = data.listing;

        setSuburb(l.suburb || "");
        setState(l.state || "");
        setPostcode(l.postcode || "");

        setAccessType(l.accessType || "Access Available Anytime");
        setStreetParking(!!l.streetParking);
        setPetsAtHome(!!l.petsAtHome);
        setAlarmCodeRequired(!!l.alarmCodeRequired);

        setPoolType(l.poolType || "");
        setPoolSize(l.poolSize || "");
        setPoolSurface(l.poolSurface || "");
        setHasSpa(!!l.hasSpa);

        setNewClient(!!l.newClient);
        setLongTermClient(!!l.longTermClient);

        setServiceDay(l.serviceDay || "any");
        setServiceTime(l.serviceTime || "any");
        setFrequency(l.frequency || "weekly");

        setServices((prev) => ({
          ...prev,
          ...(l.services || {}),
        }));

        setServiceFee(l.serviceFee || "");
        setSalePrice(l.salePrice || "");
      } catch (err) {
        console.error("❌ LOAD POOL CARE ERROR:", err);
        navigate("/my-listings");
      }
    }

    loadListing();
}, [id, navigate]);

  // ================================
  // SAVE
  // ================================
  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("");

    try {
      const token = localStorage.getItem("token");

      const res = await fetch(
        `${API_BASE_URL}/api/poolcare/listings/edit/${id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            suburb,
            state,
            postcode,
            accessType,
            streetParking,
            petsAtHome,
            alarmCodeRequired,
            poolType,
            poolSize,
            poolSurface,
            hasSpa,
            newClient,
            longTermClient,
            serviceDay,
            serviceTime,
            frequency,
            services,
            serviceFee,
            salePrice,
          }),
        }
      );

      const data = await res.json();

      if (!res.ok) {
        setMessage(data.error || "Failed to update listing");
        return;
      }

      alert("Pool Care listing updated");
      navigate("/my-listings");
    } catch (err) {
      console.error("❌ UPDATE ERROR:", err);
      setMessage("Server error updating listing");
    }
  };

  return (
    <div style={container}>
      <h2 style={pageHeader}>Edit Pool Care Listing</h2>

      {message && (
        <div style={{ textAlign: "center", fontWeight: 600 }}>{message}</div>
      )}

      <form onSubmit={handleSubmit} style={form}>
        {/* LOCATION */}
        <h3 style={sectionHeader}>Location</h3>
        <input style={input} value={suburb} onChange={(e) => setSuburb(e.target.value)} required />
        <select style={input} value={state} onChange={(e) => setState(e.target.value)} required>
          <option value="">Select State</option>
          <option value="NSW">NSW</option>
          <option value="VIC">VIC</option>
          <option value="QLD">QLD</option>
          <option value="WA">WA</option>
          <option value="SA">SA</option>
          <option value="TAS">TAS</option>
          <option value="ACT">ACT</option>
          <option value="NT">NT</option>
        </select>
        <input style={input} value={postcode} onChange={(e) => setPostcode(e.target.value)} required />

        {/* CLIENT OVERVIEW */}
        <h3 style={sectionHeaderBlue}>Client Overview</h3>
        <select style={input} value={accessType} onChange={(e) => setAccessType(e.target.value)}>
          <option>Access Available Anytime</option>
          <option>Client Must Be Home</option>
          <option>Key / Gate Code Access</option>
        </select>

        <Checkbox label="Street Parking Available" value={streetParking} setValue={setStreetParking} />
        <Checkbox label="Pets at Home" value={petsAtHome} setValue={setPetsAtHome} />
        <Checkbox label="Alarm Code Required" value={alarmCodeRequired} setValue={setAlarmCodeRequired} />

        {/* POOL DETAILS */}
        <h3 style={sectionHeader}>Pool Details</h3>

        <select style={input} value={poolSize} onChange={(e) => setPoolSize(e.target.value)} required>
          <option value="">Pool Service Type</option>
          <option value="Small">Small</option>
          <option value="Medium">Medium</option>
          <option value="Large">Large</option>
        </select>

        <select style={input} value={poolType} onChange={(e) => setPoolType(e.target.value)} required>
          <option value="">Pool Type</option>
          <option value="Above Ground">Above Ground</option>
          <option value="Inground">Inground</option>
        </select>

        <select style={input} value={poolSurface} onChange={(e) => setPoolSurface(e.target.value)} required>
          <option value="">Pool Surface</option>
          <option value="Concrete">Concrete</option>
          <option value="Vinyl Liner">Vinyl Liner</option>
          <option value="Fiberglass">Fiberglass</option>
        </select>

        <Checkbox label="Has Spa" value={hasSpa} setValue={setHasSpa} />

        {/* SERVICE */}
        <h3 style={sectionHeader}>Service Preferences</h3>

        <select style={input} value={serviceDay} onChange={(e) => setServiceDay(e.target.value)}>
          <option value="any">Any Day</option>
          <option value="monday">Monday</option>
          <option value="tuesday">Tuesday</option>
          <option value="wednesday">Wednesday</option>
          <option value="thursday">Thursday</option>
          <option value="friday">Friday</option>
          <option value="saturday">Saturday</option>
          <option value="sunday">Sunday</option>
        </select>

        <select style={input} value={serviceTime} onChange={(e) => setServiceTime(e.target.value)}>
          <option value="any">Any Time</option>
          <option value="am">Morning</option>
          <option value="pm">Afternoon</option>
        </select>

        <select style={input} value={frequency} onChange={(e) => setFrequency(e.target.value)}>
          <option value="weekly">Weekly</option>
          <option value="fortnightly">Fortnightly</option>
          <option value="every_3_weeks">Every 3 Weeks</option>
          <option value="monthly">Monthly</option>
        </select>

        {/* CLIENT HISTORY */}
<h3 style={sectionHeader}>Client History</h3>

<label style={checkboxRow}>
  <input
    type="checkbox"
    checked={newClient}
    onChange={(e) => {
      const checked = e.target.checked;
      setNewClient(checked);
      if (checked) setLongTermClient(false);
    }}
  />
  New client (under 6 months)
</label>

<label style={checkboxRow}>
  <input
    type="checkbox"
    checked={longTermClient}
    onChange={(e) => {
      const checked = e.target.checked;
      setLongTermClient(checked);
      if (checked) setNewClient(false);
    }}
  />
  Long-term client (12+ months)
</label>

        {/* SERVICES */}
        <h3 style={sectionHeaderPurple}>Pool Services & Chemicals</h3>
        {Object.keys(services).map((key) => (
          <Checkbox
            key={key}
            label={key.replace(/([A-Z])/g, " $1")}
            value={services[key]}
            setValue={() => toggleService(key)}
          />
        ))}

{/* PRICING */}
<h3 style={sectionHeaderOrange}>Pricing</h3>

<label style={{ fontWeight: "bold" }}>
  Price Per Visit ($)
</label>
<input
  style={input}
  value={serviceFee}
  onChange={(e) => setServiceFee(e.target.value)}
/>

<label style={{ fontWeight: "bold" }}>
  Sale Price ($) <span style={{ color: "#d32f2f" }}>(LOCKED)</span>
</label>
<input
  style={{ ...input, backgroundColor: "#f5f5f5", cursor: "not-allowed" }}
  value={salePrice}
  disabled
/>



        <button type="submit" style={button}>
          Save Changes
        </button>
      </form>
    </div>
  );
}

function Checkbox({ label, value, setValue }) {
  return (
    <label style={checkboxRow}>
      <input type="checkbox" checked={!!value} onChange={(e) => setValue(e.target.checked)} />
      {label}
    </label>
  );
}

const container = { maxWidth: "650px", margin: "40px auto", padding: "25px", background: "#fff", borderRadius: "12px" };
const pageHeader = { textAlign: "center", marginBottom: "25px", color: "#2e7d32" };
const form = { display: "flex", flexDirection: "column", gap: "20px" };
const input = { padding: "10px", borderRadius: "6px", border: "1px solid #ccc" };
const checkboxRow = { display: "flex", gap: "10px" };
const sectionHeader = { borderBottom: "2px solid #4CAF50" };
const sectionHeaderBlue = { borderBottom: "2px solid #1976D2" };
const sectionHeaderPurple = { borderBottom: "2px solid #9C27B0" };
const sectionHeaderOrange = { borderBottom: "2px solid #FF9800" };
const button = { padding: "14px", backgroundColor: "#2e7d32", color: "#fff", borderRadius: "8px", border: "none" };

export default EditPoolCareListings;
